import { ExclamationCircle } from 'flowbite-react-icons/outline'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import React, { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * SMART on FHIR launch landing page that handles authentication
 * @returns {JSX.Element} The launch component
 */
const Launch: React.FC = () => {
  const searchParams = useSearchParams().at(0) as URLSearchParams
  const iss = searchParams.get('iss') || ''
  const launch = searchParams.get('launch') || ''

  // Automatically trigger the launch when component mounts
  useEffect(() => {
    const SmartOnFHIRLaunch = async () => {
      try {
        const client = EpicAuthClient.getInstance()
        const redirectUrl = await client.retrieveSmartOnFHIRRedirectUrl(iss, launch)
        window.location.href = redirectUrl
      } catch (error) {
        console.error('Login error:', error)
      }
    }

    if (iss && launch) {
      const timer = setTimeout(() => {
        SmartOnFHIRLaunch()
      }, 1000)

      return () => clearTimeout(timer)
    } else {
      // For development/testing: show error message when parameters are missing
      console.warn('Missing required parameters: iss and launch are required for SMART on FHIR launch')
    }
  }, [iss, launch])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-secondary-medium dark:bg-gray-800">
      <div className="w-full max-w-md p-8">
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-8 shadow-lg">
          <div className="mt-8">
            <div className="text-center">
              {iss && launch ? (
                <>
                  <div
                    className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-600 dark:border-brand-500 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                    role="status"
                  >
                    <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                      Loading...
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-text-body dark:text-gray-400">Initiating SMART on FHIR authorization...</p>
                </>
              ) : (
                <>
                  <div className="mb-4 text-text-fg-error dark:text-error-100">
                    <ExclamationCircle className="mx-auto h-12 w-12" />
                  </div>
                  <h3 className="mb-2 text-lg font-medium text-text-heading dark:text-white">Missing Launch Parameters</h3>
                  <p className="mb-4 text-sm text-text-body dark:text-gray-400">
                    This page requires SMART on FHIR launch parameters to function properly.
                  </p>
                  <p className="text-xs text-text-body dark:text-gray-400">
                    Expected URL format: <code className="rounded bg-background-tertiary dark:bg-gray-800 px-1">/launch?iss=URL&launch=TOKEN</code>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Launch
